"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { FETCH_DRAFT_AUTOMATED_CATALOG } from "@graphql/queries/stores/catalog/fetch-draft-automated-catalog";
import useFetchSwagFlowUserConfig from "@hooks/user/use-fetch-swag-flow-user-config";
import useModifyUser from "@stadium/hooks/lib/use-modify-user";
import useManageDraftCollection from "@stadium/shared/src/hooks/catalog/use-manage-draft-collection";
import { useAuthQuery } from "@stadium/services/apollo-client";
import { showError } from "@stadium/services/react-toastify";

const DEFAULT_OBJ = {};
const DEFAULT_ARR = [];
const DEFAULT_SWAG_COLLECTION_META = {
  total: 0,
  countryWiseProductCount: DEFAULT_OBJ,
  globalProductCount: 0,
  totalColorOptions: 0,
  totalProductsCount: 0,
};

const DEFAULT_CREATE_SWAG_CONTEXT = {
  swagName: "",
  preferredColorGroupIds: DEFAULT_ARR,
  preferredColorNames: DEFAULT_ARR,
  swagCollection: DEFAULT_ARR,
  setSwagCollection: () => {},
  swagCollectionMeta: DEFAULT_SWAG_COLLECTION_META,
  setSwagCollectionMeta: () => {},
  swagLogos: DEFAULT_ARR,
  setSwagLogos: () => {},
  colorGroupsData: DEFAULT_ARR,
  draftSwagData: DEFAULT_OBJ,
  setDraftSwagData: () => {},
  draftCatalogSelectedProductsPresence: DEFAULT_OBJ,
  setDraftCatalogSelectedProductsPresence: () => {},
  setDraftCatalogSelectedProductPresence: () => {},
  isSwagCreationShopFlow: false,
  currentUserConfig: null,
  modifyUser: () => {},
  preferredColorsHex: [],
};

export const CreateSwagContext = createContext(DEFAULT_CREATE_SWAG_CONTEXT);

export const useCreateSwagContext = () => useContext(CreateSwagContext);

const CreateSwagProvider = ({ children, isSwagCreationShopFlow = false, colorGroupsData = [] }) => {
  const [draftSwagData, setDraftSwagData] = useState(DEFAULT_OBJ);
  const [swagCollection, setSwagCollection] = useState(DEFAULT_ARR);
  const [swagCollectionMeta, setSwagCollectionMeta] = useState(DEFAULT_SWAG_COLLECTION_META);
  const [swagLogos, setSwagLogos] = useState(DEFAULT_ARR);
  const [draftCatalogSelectedProductsPresence, setDraftCatalogSelectedProductsPresence] =
    useState(DEFAULT_OBJ);
  const [colorMapping, setColorMapping] = useState({});

  const {
    data: draftSwagResponse,
    loading: isLoadingDraftSwag,
    called: isDraftSwagCalled,
  } = useAuthQuery(FETCH_DRAFT_AUTOMATED_CATALOG);
  const { data: currentUserConfig, refetch: refetchUserConfigData } = useFetchSwagFlowUserConfig();
  const { modifyUser } = useModifyUser(() => refetchUserConfigData?.());
  const { saveDraftSwag } = useManageDraftCollection();

  const hasSelectedColors = draftSwagData?.colorGroupIds?.length > 0;

  useEffect(() => {
    const catalog = draftSwagResponse?.draftAutomatedCatalog;
    if (catalog) {
      setDraftSwagData((prev) => ({ ...prev, ...catalog }));
    }
  }, [draftSwagResponse]);

  useEffect(() => {
    if (!draftSwagData.id || draftSwagData.showDraftAutomatedMockupBanner) return;
    saveDraftSwag({
      id: draftSwagData.id,
      showDraftAutomatedMockupBanner: true,
    });
  }, [draftSwagData.id, draftSwagData.showDraftAutomatedMockupBanner, saveDraftSwag]);

  const allColorGroupIds = useMemo(() => {
    return colorGroupsData.map((color) => +color.id);
  }, [colorGroupsData]);

  const defaultPreferredColorGroupIds = useMemo(() => {
    return draftSwagData?.colorGroupIds || DEFAULT_ARR;
  }, [colorGroupsData, draftSwagData]);

  const preferredColorGroups = useMemo(() => {
    const groups = colorGroupsData ?? DEFAULT_ARR;
    return groups.filter(({ id }) => draftSwagData?.colorGroupIds?.includes(+id));
  }, [colorGroupsData, draftSwagData]);

  const preferredColorNames = useMemo(
    () => preferredColorGroups.map(({ name, presentation }) => ({ name, presentation })),
    [preferredColorGroups],
  );

  const newIds = useMemo(() => {
    const colorGroupIds = hasSelectedColors ? draftSwagData?.colorGroupIds : allColorGroupIds;
    return (colorGroupIds || []).filter((id) => !colorMapping[id]);
  }, [draftSwagData?.colorGroupIds, colorMapping, allColorGroupIds]);

  const fetchedPreferredColors = async () => {
    try {
      const colorGroups = hasSelectedColors ? preferredColorGroups : colorGroupsData;
      const groupsToFetch = colorGroups.filter(({ id }) => newIds?.includes(Number(id)));

      const urls = groupsToFetch.map((g) => g.colorMappingUrl).filter(Boolean);

      const responses = await Promise.all(urls.map((url) => fetch(url)));
      const data = await Promise.all(responses.map((r) => r.json()));

      const newMapping = Object.fromEntries(
        data.map((item) => [Number(item.id), Object.values(item.values).map((v) => v.hex)]),
      );
      if (!Object.keys(newMapping).length) return;
      setColorMapping((prev) => ({
        ...prev,
        ...newMapping,
      }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      showError("Failed to fetch color groups");
    }
  };

  useEffect(() => {
    if (!newIds.length) return;
    fetchedPreferredColors();
  }, [newIds]);

  const preferredColorsHex = useMemo(() => {
    const colorIds = hasSelectedColors ? draftSwagData?.colorGroupIds : allColorGroupIds;
    const allColors = (colorIds || []).flatMap((id) => colorMapping[Number(id)]).filter(Boolean);

    return allColors;
  }, [draftSwagData?.colorGroupIds, colorMapping, allColorGroupIds]);

  const setDraftCatalogSelectedProductPresence = ({ productId, isSelected, allAll = false }) => {
    if (allAll) {
      const allProductIds = Object.keys(draftCatalogSelectedProductsPresence);
      const updatedPresence = allProductIds.reduce((acc, productId) => {
        acc[productId] = true;
        return acc;
      }, {});
      setDraftCatalogSelectedProductsPresence(updatedPresence);
      return;
    }

    const normalizedProductId = Number(productId);
    if (!Number.isFinite(normalizedProductId)) return;

    setDraftCatalogSelectedProductsPresence((prevState) => ({
      ...prevState,
      [String(normalizedProductId)]: Boolean(isSelected),
    }));
  };

  const value = useMemo(
    () => ({
      swagName: draftSwagData?.name || "",
      preferredColorGroupIds: defaultPreferredColorGroupIds,
      preferredColorNames,
      swagCollection,
      setSwagCollection,
      swagCollectionMeta,
      setSwagCollectionMeta,
      swagLogos,
      setSwagLogos,
      colorGroupsData,
      draftSwagData,
      setDraftSwagData,
      draftCatalogSelectedProductsPresence,
      setDraftCatalogSelectedProductsPresence,
      setDraftCatalogSelectedProductPresence,
      isSwagCreationShopFlow,
      currentUserConfig,
      modifyUser,
      preferredColorsHex,
      isLoadingDraftSwag: isLoadingDraftSwag || !isDraftSwagCalled,
    }),
    [
      draftSwagData,
      preferredColorNames,
      swagCollection,
      swagCollectionMeta,
      swagLogos,
      draftCatalogSelectedProductsPresence,
      colorGroupsData,
      setDraftSwagData,
      isSwagCreationShopFlow,
      defaultPreferredColorGroupIds,
      currentUserConfig,
      modifyUser,
      preferredColorsHex,
      isLoadingDraftSwag,
      isDraftSwagCalled,
    ],
  );

  return <CreateSwagContext.Provider value={value}>{children}</CreateSwagContext.Provider>;
};

export default CreateSwagProvider;
